'use client';

import React, { useContext } from 'react';
import {
  BuildingsIcon,
  CloudArrowUpIcon,
  FilesIcon,
  KeyboardIcon,
  LayoutIcon,
  ListBulletsIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  QuestionIcon,
  ShieldWarningIcon,
  SignOutIcon,
  UploadSimpleIcon,
} from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { recordStat } from '@/shared/functions';
import { StatsContext } from '@/app/contexts';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import type { FeatureFlagTypes, View } from '@/shared/types';
import { isWorkProductView } from '@/shared/types';
import {
  primaryNavMenuButtonProps,
  primaryNavMenuItemClassName,
} from './primary-nav-action';

export const FeedbackButton = ({ onFeedbackClick }: { onFeedbackClick: () => void }) => {
  const t = useTimePilotT();
  return (
    <Button
      id="FeedbackButton"
      type="button"
      {...primaryNavMenuButtonProps}
      onClick={onFeedbackClick}
      className={primaryNavMenuItemClassName}
      leadingIcon={<MegaphoneIcon mirrored />}
    >
      {t('nav.feedback')}
    </Button>
  );
};

export const AccountMenuItem = ({
  selected,
  onSelect,
  onCloseMenu,
}: {
  selected: boolean;
  onSelect: () => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  return (
    <Button
      id="AccountMenuItem"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.openAccount')}
      aria-current={selected ? 'page' : undefined}
      onClick={(event) => {
        onSelect();
        onCloseMenu?.();
        recordStat('AccountButtonClicked', event, statsContext);
      }}
      className={primaryNavMenuItemClassName}
      // Buildings carries far more interior detail than the other menu glyphs, so
      // the bold weight other items use for selection reads as a blot here.
      leadingIcon={<BuildingsIcon weight="regular" />}
    >
      {t('nav.account')}
    </Button>
  );
};

export const SyncMenuItem = ({
  handleSyncData,
  isSyncing,
}: {
  handleSyncData: () => void;
  isSyncing: boolean;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  const label = isSyncing ? t('nav.syncing') : t('nav.syncData');
  return (
    <Button
      id="SyncDataButton"
      {...primaryNavMenuButtonProps}
      aria-label={label}
      disabled={isSyncing}
      onClick={(event) => {
        handleSyncData();
        recordStat('SyncDataButtonClicked', event, statsContext);
      }}
      className={primaryNavMenuItemClassName}
      leadingIcon={<CloudArrowUpIcon />}
    >
      {label}
    </Button>
  );
};

export const HotkeysMenuItem = ({
  setShowHotkeysModal,
  onCloseMenu,
}: {
  setShowHotkeysModal: (show: boolean) => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  return (
    <Button
      id="HotkeysButton"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.showHotkeys')}
      onClick={(event) => {
        setShowHotkeysModal(true);
        onCloseMenu?.();
        recordStat('OpenHotkeysButtonClicked', event, statsContext);
      }}
      className={primaryNavMenuItemClassName}
      leadingIcon={<KeyboardIcon />}
    >
      {t('nav.hotkeys')}
    </Button>
  );
};

export const DetailsMenuItem = ({
  handleViewChange,
  setLastSelectedView,
  setWasClicked,
  onCloseMenu,
}: {
  handleViewChange: (view: View) => void;
  setLastSelectedView: (view: View) => void;
  setWasClicked: (clicked: boolean) => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  return (
    <Button
      id="DetailsButton"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.switchToDetails')}
      onClick={(event) => {
        handleViewChange('table');
        setLastSelectedView('table');
        setWasClicked(false);
        onCloseMenu?.();
        recordStat('OpenDetailsButtonClicked', event, statsContext);
      }}
      className={primaryNavMenuItemClassName}
      leadingIcon={<ListBulletsIcon />}
    >
      {t('nav.details')}
    </Button>
  );
};

export const PresentationMenuItem = ({
  handleViewChange,
  setLastSelectedView,
  setWasClicked,
  onCloseMenu,
}: {
  handleViewChange: (view: View) => void;
  setLastSelectedView: (view: View) => void;
  setWasClicked: (clicked: boolean) => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  return (
    <Button
      id="PresentationButton"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.switchToPresentation')}
      onClick={(event) => {
        handleViewChange('presentation');
        setLastSelectedView('presentation');
        setWasClicked(false);
        onCloseMenu?.();
        recordStat('OpenPresentationButtonClicked', event, statsContext);
      }}
      className={primaryNavMenuItemClassName}
      leadingIcon={<LayoutIcon />}
    >
      {t('nav.presentation')}
    </Button>
  );
};

export const CourtroomMenuItem = ({
  handleViewChange,
  setLastSelectedView,
  setWasClicked,
  onCloseMenu,
}: {
  handleViewChange: (view: View) => void;
  setLastSelectedView: (view: View) => void;
  setWasClicked: (clicked: boolean) => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  return (
    <Button
      id="CourtroomButton"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.switchToCourtroom')}
      onClick={(event) => {
        handleViewChange('courtroom');
        setLastSelectedView('courtroom');
        setWasClicked(false);
        onCloseMenu?.();
        recordStat('OpenCourtroomButtonClicked', event, statsContext);
      }}
      className={primaryNavMenuItemClassName}
      leadingIcon={<LayoutIcon />}
    >
      {t('nav.courtroom')}
    </Button>
  );
};

export const AssistantMenuItem = ({
  selectedView,
  handleViewChange,
  onCloseMenu,
}: {
  featureFlags?: { [key: string]: FeatureFlagTypes | null };
  selectedView: View;
  handleViewChange: (view: View) => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  return (
    <Button
      id="AssistantMenuItem"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.openAssistant')}
      onClick={(event) => {
        handleViewChange('chat');
        onCloseMenu?.();
        recordStat('OpenAssistantButtonClicked', event, statsContext);
      }}
      className={primaryNavMenuItemClassName}
      leadingIcon={<MagnifyingGlassIcon weight={selectedView === 'chat' ? 'bold' : 'regular'} />}
    >
      {t('nav.assistant')}
    </Button>
  );
};

export const WorkProductsMenuItem = ({
  selectedView,
  handleViewChange,
  onWorkProductsClick,
  onCloseMenu,
}: {
  featureFlags?: { [key: string]: FeatureFlagTypes | null };
  selectedView: View;
  handleViewChange: (view: View) => void;
  onWorkProductsClick?: () => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  const handleClick = (event: React.MouseEvent) => {
    if (onWorkProductsClick) {
      onWorkProductsClick();
    } else {
      handleViewChange('work-product-index');
    }
    onCloseMenu?.();
    recordStat('OpenWorkProductsButtonClicked', event, statsContext);
  };
  return (
    <Button
      id="WorkProductsMenuItem"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.openWorkProducts')}
      onClick={handleClick}
      className={primaryNavMenuItemClassName}
      leadingIcon={<FilesIcon weight={isWorkProductView(selectedView) ? 'bold' : 'regular'} />}
    >
      {t('nav.workProducts')}
    </Button>
  );
};

export const LogoutMenuItem = ({
  onLogout,
  onCloseMenu,
}: {
  onLogout: () => void;
  onCloseMenu?: () => void;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  return (
    <Button
      id="LogoutMenuItem"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.logOut')}
      onClick={(event) => {
        onCloseMenu?.();
        recordStat('LogoutButtonClicked', event, statsContext);
        onLogout();
      }}
      className={primaryNavMenuItemClassName}
      leadingIcon={<SignOutIcon />}
    >
      {t('nav.logOut')}
    </Button>
  );
};

export const HelpMenuItem = ({ onCloseMenu }: { onCloseMenu?: () => void }) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  return (
    <Button
      id="HelpMenuItem"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.help')}
      className={primaryNavMenuItemClassName}
      leadingIcon={<QuestionIcon weight="regular" />}
      onClick={(event) => {
        onCloseMenu?.();
        recordStat('HelpButtonClicked', event, statsContext);
        window.open('https://intercom.help/tranquility-ai/en/', '_blank', 'noopener,noreferrer');
      }}
    >
      {t('nav.help')}
    </Button>
  );
};

export const CsamMenuItem = ({
  onSelect,
  onCloseMenu,
  selected = false,
  badgeCount,
}: {
  onSelect: () => void;
  onCloseMenu?: () => void;
  selected?: boolean;
  badgeCount?: number;
}) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  const showBadge = typeof badgeCount === 'number' && badgeCount > 0;
  return (
    <Button
      id="CsamMenuItem"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.openCsam')}
      aria-current={selected ? 'page' : undefined}
      onClick={(event) => {
        onSelect();
        onCloseMenu?.();
        recordStat('CsamDashboardButtonClicked', event, statsContext);
      }}
      className={`${primaryNavMenuItemClassName} relative`}
      leadingIcon={<ShieldWarningIcon weight={selected ? 'bold' : 'regular'} />}
    >
      {t('nav.csam')}
      {showBadge ? (
        <span
          data-testid="csam-user-flagged-badge"
          className="ml-auto inline-flex min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-4 text-white"
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      ) : null}
    </Button>
  );
};

export const UploadMenuItem = ({ onOpen }: { onOpen: () => void }) => {
  const statsContext = useContext(StatsContext);
  const t = useTimePilotT();
  return (
    <Button
      id="ImportMenuItem"
      {...primaryNavMenuButtonProps}
      aria-label={t('nav.openImport')}
      onClick={(event) => {
        onOpen();
        recordStat('UploadButtonClicked', event, statsContext);
      }}
      className={primaryNavMenuItemClassName}
      leadingIcon={<UploadSimpleIcon />}
    >
      {t('nav.import')}
    </Button>
  );
};
